/**
 * Voice primitives for the whole game. Pure Web Audio, no samples on disk —
 * every waveform, every impulse response and every noise floor is generated at
 * boot into `AudioBuffer`s and `PeriodicWave`s that live for the session.
 *
 * Two rules drive the design:
 *
 *  1. **Nothing is allocated per shot.** `OscillatorNode` and
 *     `AudioBufferSourceNode` are one-shot objects — you cannot restart them —
 *     so a naive `new OscillatorNode()` per trigger allocates three nodes at
 *     30 shots/sec and the GC pause lands right in the middle of a wave. The
 *     pool instead keeps every source free-running for the lifetime of the
 *     context and gates it with an amplitude envelope. Triggering a voice is
 *     a handful of `AudioParam` writes and nothing else.
 *
 *  2. **The master bus can never clip.** A limiter followed by a tanh soft
 *     clip sits between every bus and the destination, so forty swarmlings
 *     dying on the same frame compresses instead of tearing.
 */

// ────────────────────────────────────────────────────────────── waveforms ──

export type WaveKind = 'sine' | 'tri' | 'saw' | 'square' | 'pulse';

/**
 * Partial count for generated `PeriodicWave`s. The implementation band-limits
 * per playback frequency, so a high count buys brightness without aliasing.
 */
const PARTIALS = 64;

const waveCaches = new WeakMap<BaseAudioContext, Map<string, PeriodicWave>>();

/** Band-limited wave table. Cached per context, so this runs once per shape. */
export function periodicWave(ctx: BaseAudioContext, kind: WaveKind, width = 0.5): PeriodicWave {
  let cache = waveCaches.get(ctx);
  if (cache === undefined) { cache = new Map(); waveCaches.set(ctx, cache); }

  const w = Math.min(0.95, Math.max(0.05, width));
  const key = kind === 'pulse' ? `pulse${w.toFixed(2)}` : kind;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;

  const real = new Float32Array(PARTIALS + 1);
  const imag = new Float32Array(PARTIALS + 1);
  for (let n = 1; n <= PARTIALS; n++) {
    switch (kind) {
      case 'sine':
        imag[n] = n === 1 ? 1 : 0;
        break;
      case 'saw':
        imag[n] = (n % 2 === 0 ? -1 : 1) / n;
        break;
      case 'square':
        imag[n] = n % 2 === 1 ? 1 / n : 0;
        break;
      case 'tri':
        imag[n] = n % 2 === 1 ? (((n - 1) / 2) % 2 === 0 ? 1 : -1) / (n * n) : 0;
        break;
      case 'pulse':
        imag[n] = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * w);
        break;
    }
  }

  const wave = ctx.createPeriodicWave(real, imag, { disableNormalization: false });
  cache.set(key, wave);
  return wave;
}

/** Decorrelated stereo white noise. One buffer, shared by every noise voice. */
export function makeNoise(ctx: BaseAudioContext, seconds = 2): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  }
  return buf;
}

/**
 * Plate-ish impulse response. A real plate has dense early reflections and a
 * bright, diffuse tail with no discrete echoes, so: a handful of sparse taps
 * inside the first 40 ms, then exponentially decaying noise smoothed by a
 * one-pole lowpass that darkens as the tail progresses.
 */
export function makePlateImpulse(ctx: BaseAudioContext, seconds = 1.6, decay = 3.2): AudioBuffer {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(rate * seconds));
  const buf = ctx.createBuffer(2, len, rate);
  const preDelay = Math.floor(rate * 0.008);

  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = preDelay; i < len; i++) {
      const t = (i - preDelay) / (len - preDelay);
      const env = Math.pow(1 - t, decay);
      // Tail darkens over time: the one-pole coefficient closes as t rises.
      const a = 0.42 - 0.3 * t;
      lp += a * ((Math.random() * 2 - 1) - lp);
      d[i] = lp * env;
    }
    // Early reflections. Offsets are prime-ish per channel so the pair never
    // lines up into a comb.
    const taps = c === 0 ? [0.0031, 0.0074, 0.0129, 0.0207, 0.0311] : [0.0043, 0.0091, 0.0152, 0.0238, 0.0367];
    for (let k = 0; k < taps.length; k++) {
      const idx = preDelay + Math.floor(taps[k] * rate);
      if (idx < len) d[idx] += (k % 2 === 0 ? 0.62 : -0.48) * Math.pow(0.72, k);
    }
  }
  return buf;
}

/** Normalised tanh transfer curve. Rounds peaks instead of squaring them off. */
export function softClipCurve(drive = 1.7, n = 2048): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(n);
  const norm = Math.tanh(drive);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * drive) / norm;
  }
  return curve;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** `cancelAndHoldAtTime` where the browser has it, best-effort where it does not. */
function holdAt(p: AudioParam, t: number): void {
  if (typeof p.cancelAndHoldAtTime === 'function') {
    p.cancelAndHoldAtTime(t);
  } else {
    const v = p.value;
    p.cancelScheduledValues(t);
    p.setValueAtTime(Math.max(v, 1e-4), t);
  }
}

// ─────────────────────────────────────────────────────────────────── send ──

/**
 * Reverb + ping-pong delay, one instance per bus so the music tail follows the
 * music slider and the SFX tail follows the SFX slider.
 */
export class FxBus {
  readonly reverbIn: GainNode;
  readonly delayIn: GainNode;

  private readonly conv: ConvolverNode;
  private readonly wet: GainNode;
  private readonly dL: DelayNode;
  private readonly dR: DelayNode;
  private readonly fbL: GainNode;
  private readonly fbR: GainNode;
  private readonly tone: BiquadFilterNode;
  private readonly panL: StereoPannerNode;
  private readonly panR: StereoPannerNode;
  private readonly echoOut: GainNode;

  constructor(ctx: AudioContext, out: AudioNode, impulse: AudioBuffer, delayTime = 0.24, feedback = 0.34) {
    this.reverbIn = ctx.createGain();
    this.reverbIn.gain.value = 1;
    this.conv = ctx.createConvolver();
    this.conv.buffer = impulse;
    this.conv.normalize = true;
    this.wet = ctx.createGain();
    this.wet.gain.value = 0.9;
    this.reverbIn.connect(this.conv);
    this.conv.connect(this.wet);
    this.wet.connect(out);

    this.delayIn = ctx.createGain();
    this.delayIn.gain.value = 1;
    this.dL = ctx.createDelay(2);
    this.dR = ctx.createDelay(2);
    this.dL.delayTime.value = delayTime;
    this.dR.delayTime.value = delayTime;
    this.fbL = ctx.createGain();
    this.fbR = ctx.createGain();
    this.fbL.gain.value = feedback;
    this.fbR.gain.value = feedback;
    this.tone = ctx.createBiquadFilter();
    this.tone.type = 'lowpass';
    this.tone.frequency.value = 2600;
    this.panL = ctx.createStereoPanner();
    this.panR = ctx.createStereoPanner();
    this.panL.pan.value = -0.85;
    this.panR.pan.value = 0.85;
    this.echoOut = ctx.createGain();
    this.echoOut.gain.value = 0.85;

    // Input hits the left tap, then the tail bounces L → R → L through a
    // lowpass so repeats get darker instead of getting fizzy.
    this.delayIn.connect(this.dL);
    this.dL.connect(this.panL);
    this.dR.connect(this.panR);
    this.panL.connect(this.echoOut);
    this.panR.connect(this.echoOut);
    this.dL.connect(this.fbL);
    this.fbL.connect(this.dR);
    this.dR.connect(this.tone);
    this.tone.connect(this.fbR);
    this.fbR.connect(this.dL);
    this.echoOut.connect(out);
  }

  setDelayTime(seconds: number, at: number): void {
    const t = clamp(seconds, 0.02, 1.5);
    this.dL.delayTime.linearRampToValueAtTime(t, at + 0.08);
    this.dR.delayTime.linearRampToValueAtTime(t, at + 0.08);
  }

  setFeedback(v: number): void {
    const g = clamp(v, 0, 0.82);
    this.fbL.gain.value = g;
    this.fbR.gain.value = g;
  }

  setReverbLevel(v: number): void { this.wet.gain.value = clamp(v, 0, 2); }
  setEchoLevel(v: number): void { this.echoOut.gain.value = clamp(v, 0, 2); }
}

// ────────────────────────────────────────────────────────────────── voice ──

/**
 * One patch. Everything is optional apart from the fundamental and the hold
 * length, so a patch table reads as a diff against a plain square blip.
 *
 * Timing model: `attack → decay (to sustain × peak) → hold for dur → release`.
 * A percussive hit leaves `sustain` and `dur` at 0 and lives entirely in the
 * decay.
 */
export interface VoiceSpec {
  /** Primary oscillator shape. */
  wave?: WaveKind;
  /** Duty cycle for `pulse`, 0.05..0.95. */
  width?: number;
  freq: number;
  /** Pitch sweep target. Omit for a static pitch. */
  freqEnd?: number;
  sweepShape?: 'lin' | 'exp';
  /** Sweep duration. Defaults to the whole attack+decay+hold span. */
  sweepTime?: number;

  /** Second oscillator, for detuned stacks and sub-octaves. */
  wave2?: WaveKind;
  /** 0 = first oscillator only, 1 = second only. */
  mix2?: number;
  /** Frequency multiplier for the second oscillator. 0.5 = one octave down. */
  ratio2?: number;
  /** Cents offset for the second oscillator. */
  detune2?: number;

  /** Noise blend, 0..1, taken out of the oscillator level. */
  noise?: number;
  /** Noise playback rate. Values below 1 darken it. */
  noiseRate?: number;

  /** State-variable style shaping. `lowpass`/`bandpass`/`highpass` cover it. */
  filter?: BiquadFilterType;
  cutoff?: number;
  cutoffEnd?: number;
  q?: number;

  attack?: number;
  decay?: number;
  /** Sustain level as a fraction of peak. */
  sustain?: number;
  release?: number;
  peak?: number;
  /** Seconds held at the sustain level. Ignored when looping. */
  dur: number;

  /** Send levels, 0..1. */
  reverb?: number;
  echo?: number;

  /** Pitch/filter wobble. */
  lfoHz?: number;
  lfoCents?: number;
  lfoCutoff?: number;
}

export interface TriggerOpts {
  pan: number;
  gain: number;
  /** Extra detune in cents, applied to both oscillators. */
  detune: number;
  /** Scales every envelope stage. >1 is slower. */
  timeScale: number;
  loop: boolean;
  priority: number;
}

const DEFAULT_TRIGGER: TriggerOpts = { pan: 0, gain: 1, detune: 0, timeScale: 1, loop: false, priority: 0 };

/**
 * A permanently-wired signal path. Sources never stop; the amp envelope is the
 * only thing that decides whether the voice is audible.
 *
 * ```
 * osc  ─┐
 * osc2 ─┼─▶ filter ─▶ amp ─▶ pan ─┬─▶ dry ─▶ bus
 * noise─┘                         ├─▶ reverb send
 *                                 └─▶ echo send
 * lfo ──▶ pitch depth ─▶ osc.detune
 *     └─▶ cutoff depth ─▶ filter.frequency
 * ```
 */
export class Voice {
  /** Context time after which this voice is silent and reusable. */
  busyUntil = 0;
  /** Priority of whatever is currently playing. Higher survives stealing. */
  priority = -1;
  /** Context time the current note was scheduled at. Oldest is stolen first. */
  startedAt = 0;
  /** Bumped on every trigger so a stale handle cannot control a reused voice. */
  generation = 0;
  looping = false;

  private readonly ctx: AudioContext;
  private readonly osc: OscillatorNode;
  private readonly osc2: OscillatorNode;
  private readonly lfo: OscillatorNode;
  private readonly src: AudioBufferSourceNode;
  private readonly g1: GainNode;
  private readonly g2: GainNode;
  private readonly gn: GainNode;
  private readonly filt: BiquadFilterNode;
  private readonly amp: GainNode;
  private readonly panner: StereoPannerNode;
  private readonly dry: GainNode;
  private readonly sendVerb: GainNode;
  private readonly sendEcho: GainNode;
  private readonly lfoPitch: GainNode;
  private readonly lfoCut: GainNode;

  constructor(ctx: AudioContext, out: AudioNode, fx: FxBus, noise: AudioBuffer) {
    this.ctx = ctx;

    this.osc = ctx.createOscillator();
    this.osc2 = ctx.createOscillator();
    this.lfo = ctx.createOscillator();
    this.src = ctx.createBufferSource();
    this.src.buffer = noise;
    this.src.loop = true;

    this.g1 = ctx.createGain();
    this.g2 = ctx.createGain();
    this.gn = ctx.createGain();
    this.g1.gain.value = 0;
    this.g2.gain.value = 0;
    this.gn.gain.value = 0;

    this.filt = ctx.createBiquadFilter();
    this.filt.type = 'lowpass';
    this.filt.frequency.value = 18000;
    this.filt.Q.value = 0.7;

    this.amp = ctx.createGain();
    this.amp.gain.value = 0;

    this.panner = ctx.createStereoPanner();
    this.dry = ctx.createGain();
    this.dry.gain.value = 1;
    this.sendVerb = ctx.createGain();
    this.sendVerb.gain.value = 0;
    this.sendEcho = ctx.createGain();
    this.sendEcho.gain.value = 0;

    this.lfoPitch = ctx.createGain();
    this.lfoPitch.gain.value = 0;
    this.lfoCut = ctx.createGain();
    this.lfoCut.gain.value = 0;

    this.osc.connect(this.g1).connect(this.filt);
    this.osc2.connect(this.g2).connect(this.filt);
    this.src.connect(this.gn).connect(this.filt);
    this.filt.connect(this.amp).connect(this.panner);
    this.panner.connect(this.dry).connect(out);
    this.panner.connect(this.sendVerb).connect(fx.reverbIn);
    this.panner.connect(this.sendEcho).connect(fx.delayIn);

    this.lfo.connect(this.lfoPitch);
    this.lfoPitch.connect(this.osc.detune);
    this.lfoPitch.connect(this.osc2.detune);
    this.lfo.connect(this.lfoCut);
    this.lfoCut.connect(this.filt.frequency);

    this.osc.setPeriodicWave(periodicWave(ctx, 'square'));
    this.osc2.setPeriodicWave(periodicWave(ctx, 'square'));
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 5;

    const t0 = ctx.currentTime;
    this.osc.start(t0);
    this.osc2.start(t0);
    this.lfo.start(t0);
    this.src.start(t0);
  }

  /** Fast fade so a stolen voice does not click on the way out. */
  cut(at: number): void {
    const now = Math.max(at, this.ctx.currentTime);
    const g = this.amp.gain;
    holdAt(g, now);
    g.linearRampToValueAtTime(0, now + 0.004);
    this.looping = false;
    this.busyUntil = now + 0.004;
  }

  trigger(s: VoiceSpec, when: number, o: TriggerOpts = DEFAULT_TRIGGER): void {
    const ctx = this.ctx;
    let t = Math.max(when, ctx.currentTime);
    if (this.busyUntil > t + 1e-4) { this.cut(t); t += 0.005; }
    this.generation++;
    this.looping = o.loop;
    this.priority = o.priority;
    this.startedAt = t;

    const ts = o.timeScale;
    const atk = Math.max(0.0005, (s.attack ?? 0.002) * ts);
    const dec = Math.max(0.002, (s.decay ?? 0.06) * ts);
    const sus = clamp(s.sustain ?? 0, 0, 1);
    const rel = Math.max(0.006, (s.release ?? 0.05) * ts);
    const hold = o.loop ? 0 : Math.max(0, s.dur * ts);
    const peak = Math.max(0.0004, (s.peak ?? 0.5) * o.gain);
    const susLvl = Math.max(1e-4, peak * sus);
    const span = atk + dec + hold;

    // Timbre. Safe to set immediately: the pool only hands out voices that
    // have already finished sounding.
    this.osc.setPeriodicWave(periodicWave(ctx, s.wave ?? 'square', s.width ?? 0.5));
    const mix2 = clamp(s.mix2 ?? 0, 0, 1);
    if (mix2 > 0) this.osc2.setPeriodicWave(periodicWave(ctx, s.wave2 ?? s.wave ?? 'square', s.width ?? 0.5));

    const noiseMix = clamp(s.noise ?? 0, 0, 1);
    const oscLvl = 1 - noiseMix;
    this.g1.gain.setValueAtTime(oscLvl * (1 - mix2), t);
    this.g2.gain.setValueAtTime(oscLvl * mix2, t);
    this.gn.gain.setValueAtTime(noiseMix, t);

    // Pitch.
    const f0 = Math.max(1, s.freq);
    const f1 = Math.max(1, s.freqEnd ?? f0);
    const ratio = s.ratio2 ?? 1;
    const sweepT = Math.max(0.004, s.sweepTime !== undefined ? s.sweepTime * ts : span);

    const p1 = this.osc.frequency;
    p1.cancelScheduledValues(t);
    p1.setValueAtTime(f0, t);
    const p2 = this.osc2.frequency;
    p2.cancelScheduledValues(t);
    p2.setValueAtTime(f0 * ratio, t);
    if (f1 !== f0) {
      if (s.sweepShape === 'lin') {
        p1.linearRampToValueAtTime(f1, t + sweepT);
        p2.linearRampToValueAtTime(f1 * ratio, t + sweepT);
      } else {
        p1.exponentialRampToValueAtTime(f1, t + sweepT);
        p2.exponentialRampToValueAtTime(f1 * ratio, t + sweepT);
      }
    }
    this.osc.detune.setValueAtTime(o.detune, t);
    this.osc2.detune.setValueAtTime((s.detune2 ?? 0) + o.detune, t);
    this.src.playbackRate.setValueAtTime(clamp(s.noiseRate ?? 1, 0.05, 8), t);

    // Filter.
    this.filt.type = s.filter ?? 'lowpass';
    this.filt.Q.setValueAtTime(Math.max(0.0001, s.q ?? 0.7), t);
    const fc = this.filt.frequency;
    const c0 = clamp(s.cutoff ?? 18000, 20, 20000);
    fc.cancelScheduledValues(t);
    fc.setValueAtTime(c0, t);
    if (s.cutoffEnd !== undefined) {
      fc.exponentialRampToValueAtTime(clamp(s.cutoffEnd, 20, 20000), t + sweepT);
    }

    // Modulation.
    this.lfo.frequency.setValueAtTime(Math.max(0.01, s.lfoHz ?? 5), t);
    this.lfoPitch.gain.setValueAtTime(s.lfoCents ?? 0, t);
    this.lfoCut.gain.setValueAtTime(s.lfoCutoff ?? 0, t);

    // Routing.
    this.panner.pan.setValueAtTime(clamp(o.pan, -1, 1), t);
    this.sendVerb.gain.setValueAtTime(clamp(s.reverb ?? 0, 0, 1), t);
    this.sendEcho.gain.setValueAtTime(clamp(s.echo ?? 0, 0, 1), t);

    // Envelope.
    const g = this.amp.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(1e-4, t);
    g.linearRampToValueAtTime(peak, t + atk);
    g.exponentialRampToValueAtTime(susLvl, t + atk + dec);
    if (o.loop) {
      this.busyUntil = Number.POSITIVE_INFINITY;
    } else {
      const relAt = t + span;
      g.setValueAtTime(susLvl, relAt);
      g.exponentialRampToValueAtTime(1e-4, relAt + rel);
      g.linearRampToValueAtTime(0, relAt + rel + 0.004);
      this.busyUntil = relAt + rel + 0.01;
    }
  }

  /** Ends a looping voice with a proper tail. */
  release(at: number, seconds = 0.12): void {
    const now = Math.max(at, this.ctx.currentTime);
    const g = this.amp.gain;
    holdAt(g, now);
    g.exponentialRampToValueAtTime(1e-4, now + seconds);
    g.linearRampToValueAtTime(0, now + seconds + 0.004);
    this.looping = false;
    this.busyUntil = now + seconds + 0.01;
  }

  setFreq(hz: number, at: number, glide = 0.05): void {
    const f = Math.max(1, hz);
    this.osc.frequency.linearRampToValueAtTime(f, at + glide);
    this.osc2.frequency.linearRampToValueAtTime(f, at + glide);
  }

  setCutoff(hz: number, at: number, glide = 0.05): void {
    this.filt.frequency.linearRampToValueAtTime(clamp(hz, 20, 20000), at + glide);
  }

  setGain(v: number, at: number, glide = 0.05): void {
    this.amp.gain.linearRampToValueAtTime(Math.max(0, v), at + glide);
  }

  setPan(v: number, at: number, glide = 0.03): void {
    this.panner.pan.linearRampToValueAtTime(clamp(v, -1, 1), at + glide);
  }
}

/**
 * Fixed-size voice bank with priority stealing.
 *
 * Free means *finished sounding by now*, not *finished by the time the note
 * starts*. Timbre setup (`setPeriodicWave`, filter type) applies immediately
 * rather than on the audio timeline, so re-tasking a voice that is still
 * ringing would mutate the note already in flight.
 */
export class VoicePool {
  readonly voices: Voice[] = [];

  constructor(ctx: AudioContext, out: AudioNode, fx: FxBus, noise: AudioBuffer, size: number) {
    for (let i = 0; i < size; i++) this.voices.push(new Voice(ctx, out, fx, noise));
  }

  /**
   * Returns an idle voice, or the least important sounding one if every voice
   * is busy. Returns `null` when everything playing outranks the request —
   * dropping a swarmling death is better than punching a hole in a boss roar.
   */
  alloc(priority: number, now: number): Voice | null {
    const list = this.voices;
    let victim: Voice | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    for (let i = 0; i < list.length; i++) {
      const v = list[i];
      if (v.busyUntil <= now) return v;
      if (v.priority > priority) continue;
      // Lowest priority first, oldest within a priority.
      const score = v.priority * 1e6 + v.startedAt;
      if (score < bestScore) { bestScore = score; victim = v; }
    }
    return victim;
  }

  /** How many voices are sounding. Used by the throttle and the dev overlay. */
  activeCount(now: number): number {
    let n = 0;
    for (let i = 0; i < this.voices.length; i++) if (this.voices[i].busyUntil > now) n++;
    return n;
  }

  silenceAll(now: number): void {
    for (let i = 0; i < this.voices.length; i++) {
      if (this.voices[i].busyUntil > now) this.voices[i].cut(now);
    }
  }
}

// ───────────────────────────────────────────────────────────────── engine ──

export interface EngineOptions {
  sfxVoices?: number;
  musicVoices?: number;
}

/**
 * Owns the context graph: three user-facing buses, a duck stage under the
 * music bus, per-bus sends, and the limiter/soft-clip pair guarding the
 * destination.
 *
 * ```
 * sfx voices ─▶ sfxFx ──┐
 *            ─▶ sfxBus ─┼─┐
 * mus voices ─▶ musFx ──┤ │
 *            ─▶ musBus ─┴─┴─▶ duck ─▶ pre ─▶ limiter ─▶ soft clip ─▶ master ─▶ out
 * ```
 * (the duck stage only sits under the music path; SFX join at `pre`)
 */
export class SynthEngine {
  readonly ctx: AudioContext;
  readonly sfx: VoicePool;
  readonly music: VoicePool;
  readonly sfxFx: FxBus;
  readonly musicFx: FxBus;

  private readonly masterGain: GainNode;
  private readonly musicGain: GainNode;
  private readonly sfxGain: GainNode;
  private readonly duckGain: GainNode;
  private readonly pre: GainNode;
  private readonly limiter: DynamicsCompressorNode;
  private readonly shaper: WaveShaperNode;
  private duckUntil = 0;

  constructor(ctx: AudioContext, opts: EngineOptions = {}) {
    this.ctx = ctx;

    this.masterGain = ctx.createGain();
    this.shaper = ctx.createWaveShaper();
    this.shaper.curve = softClipCurve(1.7);
    this.shaper.oversample = '2x';
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -8;
    this.limiter.knee.value = 6;
    this.limiter.ratio.value = 14;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.18;
    this.pre = ctx.createGain();

    this.pre.connect(this.limiter);
    this.limiter.connect(this.shaper);
    this.shaper.connect(this.masterGain);
    this.masterGain.connect(ctx.destination);

    this.sfxGain = ctx.createGain();
    this.sfxGain.connect(this.pre);

    this.duckGain = ctx.createGain();
    this.duckGain.connect(this.pre);
    this.musicGain = ctx.createGain();
    this.musicGain.connect(this.duckGain);

    const impulse = makePlateImpulse(ctx, 1.6, 3.2);
    const noise = makeNoise(ctx, 2);
    this.sfxFx = new FxBus(ctx, this.sfxGain, impulse, 0.19, 0.3);
    this.musicFx = new FxBus(ctx, this.musicGain, impulse, 0.26, 0.38);

    this.sfx = new VoicePool(ctx, this.sfxGain, this.sfxFx, noise, opts.sfxVoices ?? 28);
    this.music = new VoicePool(ctx, this.musicGain, this.musicFx, noise, opts.musicVoices ?? 16);
  }

  get now(): number { return this.ctx.currentTime; }

  setMaster(v: number): void { this.masterGain.gain.setTargetAtTime(clamp(v, 0, 1.5), this.now, 0.02); }
  setMusic(v: number): void { this.musicGain.gain.setTargetAtTime(clamp(v, 0, 1.5), this.now, 0.02); }
  setSfx(v: number): void { this.sfxGain.gain.setTargetAtTime(clamp(v, 0, 1.5), this.now, 0.02); }

  /**
   * Pulls the music down under an explosion, then walks it back up. Overlapping
   * ducks extend rather than restack, so a chain of blasts holds the music
   * down once instead of stuttering it.
   */
  duck(ms: number, amount = 0.55): void {
    const now = this.now;
    const target = clamp(1 - amount, 0.05, 1);
    const until = now + ms / 1000;
    if (until <= this.duckUntil) return;
    this.duckUntil = until;
    const g = this.duckGain.gain;
    holdAt(g, now);
    g.linearRampToValueAtTime(target, now + 0.015);
    g.setValueAtTime(target, until);
    g.linearRampToValueAtTime(1, until + 0.22);
  }

  /** Cuts every sounding voice. Used on defeat/victory and on dispose. */
  panic(): void {
    const now = this.now;
    this.sfx.silenceAll(now);
    this.music.silenceAll(now);
  }
}
